import React, { forwardRef } from 'react'
import { TEST_IDS } from 'transfix-form/forms/SavedLaneForm/constants'
import { IAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase'
import AutocompleteUser, { IAutocompleteUserProps } from 'transfix-ui/components/AutocompleteUser'
import { ICarrierApplicationUserFragment } from 'transfix-ui/components/AutocompleteUser/graphql/CarrierUser'
import { ICarrierUserTypes, IUserTypeTypes } from 'types/graphqlTypes'

const RECIPIENT_USER_TYPES = [ICarrierUserTypes.dispatcher, ICarrierUserTypes.owner_operator]

const generateCarrierUserOption = ({
  email,
  fullName,
  parentUserId,
}: ICarrierApplicationUserFragment) => ({
  id: parentUserId,
  text: `${fullName} · ${email}`,
  name: fullName,
  email,
})

const getOptionsConfig: IAutocompleteUserProps['getOptionsConfig'] = data => {
  const { carrierApplicationUsers: users = [] } = data || {}
  const dispatchers = users.filter(({ userType }) => userType === IUserTypeTypes.dispatcher)
  const ownerOperators = users.filter(({ userType }) => userType === IUserTypeTypes.owner_operator)
  return [
    {
      options: dispatchers,
      groupIndex: 0,
      groupName: 'Dispatchers',
      mapOption: generateCarrierUserOption,
    },
    {
      options: ownerOperators,
      groupIndex: 1,
      groupName: 'Owner Operators',
      mapOption: generateCarrierUserOption,
    },
  ]
}

type TRecipientValue = Pick<IAutocompleteBaseOption, 'id' | 'text'> & {
  name?: string
  email?: string
}

export interface IAutocompleteRecipientUserProps
  extends Pick<IAutocompleteUserProps<TRecipientValue>, 'onChange' | 'inputValidationMessage'> {
  carrierApplicationId: string
}

const AutocompleteRecipientUser = forwardRef<HTMLInputElement, IAutocompleteRecipientUserProps>(
  ({ carrierApplicationId, inputValidationMessage, onChange }, ref) => {
    return (
      <AutocompleteUser
        ref={ref}
        value={null}
        onChange={onChange}
        getOptionsConfig={getOptionsConfig}
        inputValidationMessage={inputValidationMessage}
        inputError={!!inputValidationMessage}
        label='Recipient Name'
        placeholder='Select a recipient'
        renderInputType='withAvatar'
        dataTest={TEST_IDS.recipientAutocomplete}
        variables={{
          carrierApplicationId,
          userTypes: RECIPIENT_USER_TYPES,
        }}
        required
      />
    )
  }
)

export default AutocompleteRecipientUser
